import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ClearanceDto } from '../../../auth/dto/clearance-dto';
import { UserDto } from '../../../auth/user.dto';
import { ConfirmationComponent } from '../../../shared/confirmation/confirmation.component';
import { DisplayLabel } from '../../../shared/models/displayLabel';
import { DisplayStructure } from '../../../shared/models/displayStructure';
import { ProfileType, labelsByProfileType } from '../../../shared/models/enums/profile-type.enum';
import { AreaService } from '../../../shared/services/area.service';
import { ClearanceService } from '../../../shared/services/clearance.service';
import { DateService } from '../../../shared/services/date.service';
import { StringUtils } from '../../../shared/services/stringUtils.service';
import { StructureService } from '../../../shared/services/structure.service';
import { UserService } from '../../../shared/services/user.service';
import { ToastService } from '../../../shared/toast/toast.service';
import { Utils } from '../../../shared/utils';
import { DateValidators } from '../../../shared/validators/date.validators';

@Component({
  selector: 'app-user-details',
  templateUrl: './user-details.component.html',
})
export class UserDetailsComponent implements OnInit, OnDestroy {
  public userProfileForm: FormGroup<{
    active: FormControl<boolean>;
    lastname: FormControl<string>;
    firstname: FormControl<string>;
    email: FormControl<string>;
    profile: FormControl<string>;
    structure: FormControl<number | null>;
    cliIds: FormControl<number[]>;
    ctmIds: FormControl<number[]>;
    clearances: FormControl<string[]>;
    structureAddresses: FormControl<number[]>;
    inactiveSince: FormControl<Date>;
    replacedByUserId: FormControl<number>;
  }>;
  public submitted: boolean = false;
  public user: UserDto;
  public isModificationMode: boolean = false;

  public readonly profileTypes: Map<ProfileType, string> = labelsByProfileType;
  public readonly StringUtils: StringUtils = StringUtils;

  public clis: DisplayLabel[] = [];
  public ctms: DisplayLabel[] = [];
  public structures: DisplayStructure[] = [];
  public clearances: ClearanceDto[] = [];
  public allClearances: ClearanceDto[] = [];
  public allStructureSites: DisplayLabel[] = [];
  public referents: UserDto[] = [];

  private readonly destroy$: Subject<void> = new Subject();

  private userId: number;
  private readonly currentDate: Date;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly modal: NgbActiveModal,
    private readonly areaService: AreaService,
    private readonly structureService: StructureService,
    private readonly userService: UserService,
    private readonly clearanceService: ClearanceService,
    private readonly toastService: ToastService,
    private readonly dateService: DateService,
    private readonly modalService: NgbModal,
  ) {
    this.currentDate = this.dateService.getCurrent();
  }

  public onInit(userId?: number): void {
    this.userId = userId;
    this.isModificationMode = !!userId;
  }

  public async ngOnInit(): Promise<void> {
    await this.initList();
    return this.loadForm();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async loadForm(): Promise<void> {
    this.user = this.userId ? await this.userService.findUser(this.userId) : new UserDto();
    if (this.user.groups.includes('STRUCTURE') && this.user.structureId) {
      this.reloadReferents(this.user.structureId);
    }
    const [profile]: string[] = this.user.groups;

    this.userProfileForm = this.formBuilder.group({
      active: [this.user.active],
      lastname: [this.user.lastname, [Validators.required, Validators.pattern(Utils.alphabeticalPattern)]],
      firstname: [this.user.firstname, [Validators.required, Validators.pattern(Utils.alphabeticalPattern)]],
      email: [this.user.email, [Validators.email, Validators.required]],
      profile: [profile, Validators.required],
      structure: [this.user.structureId],
      cliIds: [this.user.cliIds],
      ctmIds: [this.user.ctmIds],
      clearances: [this.user.clearances],
      structureAddresses: [this.user.structureAddressIds, Validators.required],
      inactiveSince: [
        this.user.inactiveSince,
        [Validators.required, DateValidators.dateFormatValidator, DateValidators.pastDateValidator(this.dateService.getCurrent())],
      ],
      replacedByUserId: [this.user.replacedByUserId],
    });

    if (!this.isModificationMode) {
      this.initForm();
    } else {
      this.disableFields();
      this.onProfileValueChanged(profile);
      this.initStructureAddresses();
    }
    this.subscribeValueChanges();
  }

  private initForm(): void {
    this.disableStructure();
    this.disableClis();
    this.disableCtms();
    this.disableClearances();
    this.disableStructureAddresses();

    this.userProfileForm.controls.inactiveSince.disable();
    this.userProfileForm.controls.replacedByUserId.disable();

    this.userProfileForm.updateValueAndValidity();
  }

  private disableFields(): void {
    this.userProfileForm.controls.clearances.disable();
    if (this.user.active) {
      this.disableAndResetInactiveSince();
      this.disableAndResetReplacedByUserId();
    } else {
      this.userProfileForm.controls.inactiveSince.disable();
      this.userProfileForm.controls.replacedByUserId.disable();
    }
    this.userProfileForm.updateValueAndValidity();
  }

  private async initList(): Promise<void> {
    [this.clis, this.ctms] = await this.areaService.findAllClisAndCtms();
    this.structures = await this.structureService.findAll();
    this.allClearances = await this.clearanceService.findAll();
  }

  private initStructureAddresses() {
    if (this.user?.structureId && this.user.structureAddressIds) {
      this.allStructureSites = this.structures.find((struct) => struct.id === this.user.structureId).addresses;
      this.allStructureSites.sort((a, b) => a.label.localeCompare(b.label));
    }
  }

  private subscribeValueChanges(): void {
    this.userProfileForm.controls.profile.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: string) => this.onProfileValueChanged(value));
    this.userProfileForm.controls.structure.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: number) => this.onStructureValueChanged(value));
    this.userProfileForm.controls.active.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: boolean) => this.onActiveValueChanged(value));
  }

  public async onSubmit(): Promise<void> {
    this.submitted = true;
    if (this.userProfileForm.valid) {
      if (
        !this.userProfileForm.controls.active.value &&
        !this.userProfileForm.controls.replacedByUserId.value &&
        this.userProfileForm.controls.profile.value === 'STRUCTURE'
      ) {
        const confirm: boolean = await this.openConfirmationModal();
        if (!confirm) {
          return;
        }
      }
      try {
        if (this.isModificationMode) {
          await this.userService.updateUser({
            id: this.userId,
            email: Utils.trim(this.userProfileForm.controls.email.value),
            groups: [this.userProfileForm.controls.profile.value],
            firstname: Utils.trim(this.userProfileForm.controls.firstname.value),
            lastname: Utils.trim(this.userProfileForm.controls.lastname.value),
            active: this.userProfileForm.controls.active.value,
            structureId: this.userProfileForm.controls.structure.value,
            cliIds: this.userProfileForm.controls.cliIds.value,
            ctmIds: this.userProfileForm.controls.ctmIds.value,
            clearances: this.userProfileForm.controls.clearances.value,
            structureAddressIds: this.userProfileForm.controls.structureAddresses.value,
            inactiveSince: this.userProfileForm.controls.inactiveSince.value,
            replacedByUserId: this.userProfileForm.controls.replacedByUserId.value,
          } as UserDto);
          this.modal.close();
        } else {
          await this.userService.createUser({
            email: Utils.trim(this.userProfileForm.controls.email.value).toLowerCase(),
            groups: [this.userProfileForm.controls.profile.value],
            firstname: Utils.trim(this.userProfileForm.controls.firstname.value),
            lastname: Utils.trim(this.userProfileForm.controls.lastname.value),
            structureId: this.userProfileForm.controls.structure.value,
            cliIds: this.userProfileForm.controls.cliIds.value,
            ctmIds: this.userProfileForm.controls.ctmIds.value,
            active: Boolean(this.userProfileForm.controls.active.value),
            clearances: [...this.userProfileForm.controls.clearances.value],
            structureAddressIds: this.userProfileForm.controls.structureAddresses.value,
            inactiveSince: this.userProfileForm.controls.inactiveSince.value,
            replacedByUserId: this.userProfileForm.controls.replacedByUserId.value,
          } as UserDto);
          this.modal.close();
        }
      } catch (error) {
        if (error.status === 409) {
          this.toastService.show("L'adresse email est utilisée par un autre utilisateur.", 'danger');
        }
      }
    }
  }

  public cancel(): void {
    this.modal.close();
  }

  private onProfileValueChanged(value: string): void {
    switch (value) {
      case 'ADMIN':
      case 'DIRECTION':
      case 'DIRECTION+':
        {
          this.disableStructure();
          this.disableClis();
          this.disableCtms();
          this.disableStructureAddresses();
        }
        break;
      case 'STRUCTURE':
        {
          this.enableStructure();
          this.disableClis();
          this.disableCtms();
          this.disableStructureAddresses();
        }
        break;
      case 'RECEPTION':
        {
          this.enableStructure();
          this.disableClis();
          this.disableCtms();
          this.enableStructureAddresses();
        }
        break;
      case 'TERRITORY':
        {
          this.enableClis();
          this.disableCtms();
          this.disableStructure();
          this.disableStructureAddresses();
        }
        break;
      case 'MMIE':
        {
          this.enableCtms();
          this.disableClis();
          this.disableStructure();
          this.disableStructureAddresses();
        }
        break;
      default:
        break;
    }
    this.updateClearances(value);
    this.userProfileForm.updateValueAndValidity();
  }

  private async onStructureValueChanged(value: number): Promise<void> {
    this.userProfileForm.controls.structureAddresses.reset([]);
    if (value && this.userProfileForm.controls.profile.value === 'RECEPTION') {
      this.allStructureSites = this.structures.find((struct) => struct.id === value)?.addresses;
      this.allStructureSites.sort((a, b) => a.label.localeCompare(b.label));
    }
    if (value && this.userProfileForm.controls.profile.value === 'STRUCTURE') {
      this.reloadReferents(value);
    }
  }

  private async onActiveValueChanged(value: boolean): Promise<void> {
    if (this.userProfileForm.controls.profile.value === 'STRUCTURE') {
      if (value) {
        this.disableAndResetInactiveSince();
        this.disableAndResetReplacedByUserId();
      } else {
        this.userProfileForm.controls.inactiveSince.enable();
        this.userProfileForm.controls.replacedByUserId.enable();

        this.userProfileForm.controls.inactiveSince.setValue(this.currentDate);
      }
    }
  }

  private updateClearances(profile: string): void {
    this.clearances = this.allClearances
      .filter((item: ClearanceDto) => item.groups?.includes(profile))
      .sort((a: ClearanceDto, b: ClearanceDto) => Utils.localeSort(a.categoryLabel, b.categoryLabel) + Utils.localeSort(a.label, b.label));

    if (this.clearances?.length > 0) {
      this.enableClearances();
    } else {
      this.disableClearances();
    }
  }

  private async openConfirmationModal(): Promise<boolean> {
    const modal: NgbModalRef = this.modalService.open(ConfirmationComponent, {
      backdrop: 'static',
      centered: true,
    });
    const modalComponent: ConfirmationComponent = modal.componentInstance as ConfirmationComponent;
    return modalComponent.confirm('Vous allez désactiver cet utilisateur sans saisir de remplaçant. Êtes-vous sûr(e) ?');
  }

  private enableStructure(): void {
    this.userProfileForm.controls.structure.setValidators([Validators.required]);
    this.userProfileForm.controls.structure.enable();
  }

  private enableClis(): void {
    this.userProfileForm.controls.cliIds.setValidators([Validators.required]);
    this.userProfileForm.controls.cliIds.enable();
  }

  private enableCtms(): void {
    this.userProfileForm.controls.ctmIds.setValidators([Validators.required]);
    this.userProfileForm.controls.ctmIds.enable();
  }

  private enableClearances(): void {
    this.userProfileForm.controls.clearances.enable();
  }

  private enableStructureAddresses(): void {
    this.userProfileForm.controls.structureAddresses.setValidators([Validators.required]);
    this.userProfileForm.controls.structureAddresses.enable();
  }

  private disableStructure(): void {
    this.userProfileForm.controls.structure.reset();
    this.userProfileForm.controls.structure.clearValidators();
    this.userProfileForm.controls.structure.disable();
  }

  private disableClis(): void {
    this.userProfileForm.controls.cliIds.reset();
    this.userProfileForm.controls.cliIds.clearValidators();
    this.userProfileForm.controls.cliIds.disable();
  }

  private disableCtms(): void {
    this.userProfileForm.controls.ctmIds.reset();
    this.userProfileForm.controls.ctmIds.clearValidators();
    this.userProfileForm.controls.ctmIds.disable();
  }

  private disableClearances(): void {
    this.userProfileForm.controls.clearances.reset([]);
    this.userProfileForm.controls.clearances.clearValidators();
    this.userProfileForm.controls.clearances.disable();
  }

  private disableStructureAddresses(): void {
    this.userProfileForm.controls.structureAddresses.reset([]);
    this.userProfileForm.controls.structureAddresses.clearValidators();
    this.userProfileForm.controls.structureAddresses.disable();
  }

  private disableAndResetInactiveSince(): void {
    this.userProfileForm.controls.inactiveSince.reset();
    this.userProfileForm.controls.inactiveSince.disable();
  }

  private disableAndResetReplacedByUserId(): void {
    this.userProfileForm.controls.replacedByUserId.reset();
    this.userProfileForm.controls.replacedByUserId.disable();
  }

  private async reloadReferents(structureId: number): Promise<void> {
    this.referents = await this.userService.findAll(structureId);
  }
}
